/**
 * Input Sanitization Utilities
 * Removes dangerous and hidden Unicode characters from user input
 */

/**
 * Dangerous Unicode characters that should be removed
 * These are often invisible and can cause security issues or data corruption
 */
const DANGEROUS_UNICODE_PATTERNS = [
  // Zero-width characters (invisible)
  /[\u200B\u200C\u200D\uFEFF]/g,
  
  // Directional formatting (used for spoofing)
  /[\u202A-\u202E\u2066-\u2069]/g,
  
  // Other invisible/problematic characters
  /[\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180E]/g,
  
  // Control characters (except \n, \r which are preserved; \t is converted to space)
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g,
];

/**
 * Quick check if string contains any dangerous Unicode
 * Used for performance optimization - skip sanitization if clean
 * Note: Includes \t (U+0009) for removal, excludes \n (U+000A), \r (U+000D) as these are preserved
 */
// eslint-disable-next-line no-control-regex
const QUICK_CHECK_PATTERN = /[\u0000-\u0009\u000B\u000C\u000E-\u001F\u007F-\u009F\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180E\u200B-\u200D\u202A-\u202E\u2066-\u2069\uFEFF]/;

/**
 * Main sanitization function
 * @param {string} input - The text to sanitize
 * @param {Object} options - Configuration options
 * @param {boolean} options.preserveNewlines - Keep \n and \r (default: true)
 * @param {boolean} options.preserveTabs - Keep \t (default: true)
 * @returns {string} - Sanitized text
 */
export const sanitizeInput = (input, options = {}) => {
  if (typeof input !== 'string' || !input) {
    return input;
  }

  // Quick check - if no dangerous chars, return as-is (performance optimization)
  if (!QUICK_CHECK_PATTERN.test(input)) {
    return input;
  }

  const {
    preserveNewlines = true,
    preserveTabs = false,
  } = options;

  let sanitized = input;

  // Apply all dangerous unicode patterns
  for (const pattern of DANGEROUS_UNICODE_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  // Remove newlines if not preserving
  if (!preserveNewlines) {
    sanitized = sanitized.replace(/[\n\r]/g, '');
  }

  // Remove tabs if not preserving
  if (!preserveTabs) {
    sanitized = sanitized.replace(/\t/g, ' ');
  }

  return sanitized;
};

/**
 * Check if text contains dangerous Unicode characters
 * @param {string} input - The text to check
 * @returns {boolean} - True if dangerous characters found
 */
export const containsDangerousUnicode = (input) => {
  if (typeof input !== 'string' || !input) {
    return false;
  }
  return QUICK_CHECK_PATTERN.test(input);
};

/**
 * Get count of dangerous characters in text
 * @param {string} input - The text to analyze
 * @returns {number} - Count of dangerous characters
 */
export const countDangerousUnicode = (input) => {
  if (typeof input !== 'string' || !input) {
    return 0;
  }
  
  const matches = input.match(QUICK_CHECK_PATTERN);
  return matches ? matches.length : 0;
};

export default sanitizeInput;

